import React, { useState, useEffect, useMemo, useRef, useLayoutEffect } from "react";
import DataTable from "react-data-table-component";
import apiClient from "../api/apiClient";
import Select from "react-select";
import * as pdfMake from "pdfmake/build/pdfmake";
import * as pdfFonts from "pdfmake/build/vfs_fonts";

pdfMake.vfs = pdfFonts?.default?.vfs || pdfFonts.vfs;

// Constantes
const SERVICE_TYPES = [
  { value: "DM", label: "Desayuno/Merienda" },
  { value: "AC", label: "Almuerzo/Cena" },
  { value: "LIM", label: "Limpieza" },
  { value: "UTE", label: "Utensilios" },
  { value: "EQU", label: "Equipamiento" },
];

const PlanifLines = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  //const [filteredOrders, setFilteredOrders] = useState([]);
  const [entDate, setEntDate] = useState("");
  const [serviceType, setserviceType] = useState(null);
  const [project, setproject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [start_route, setStartRoute] = useState("");
  const [end_route, setEndRoute] = useState("");

  // cantidades preparadas por OV+Item (clave: `${DocEntry}|${ItemCode}|${UM}`)
  const [preparedByItem, setPreparedByItem] = useState({});
  const preparedBufferRef = useRef({});
  // valores visibles en los inputs (controlados por clave ItemCode|UM)
  const [inputValues, setInputValues] = useState({});
  // Scroll del expandible persistente entre (posibles) re-mounts
  const expandedScrollTopRef = useRef(0);

  // Artículos alternativos
  const [altModal, setAltModal] = useState(null); // { row, alternatives: [] }
  const [altLoading, setAltLoading] = useState(false);
  const [altSubmitting, setAltSubmitting] = useState(false);


  // (El scroll se preserva ahora dentro del contenedor de la tabla expandida)

  // ---- Cargar proyectos una sola vez
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await apiClient.get("/sales-orders/projects");
        const data = Array.isArray(res.data) ? res.data : [];
        setProjects(
          data.map((p) => ({ value: p.PrjCode, label: `${p.PrjName}` }))
        );
      } catch (e) {
        console.error(e);
        setError("Error al obtener los proyectos.");
      }
    };
    fetchProjects();
  }, []);

  // ---- Obtener consolidado
  const fetchInsumos = async () => {
    setLoading(true);
    setError("");
    const sapUser = JSON.parse(localStorage.getItem("sapUser"));
    const Company = sapUser?.CompanyDB || "";
    try {
      const { data } = await apiClient.get("/planif/consolidado/preparacion", {
        params: {
          project: project?.value ?? "",
          servicio: serviceType?.value ?? "",
          start_route,
          end_route,
          entDate,
          company: Company,
        },
      });

      const arr = Array.isArray(data) ? data : [];
      if (!arr.length) {
        setOrders([]);
        setPreparedByItem({});
        preparedBufferRef.current = {};
        return;
      }

      // 1 SOLO grupo con TODAS las líneas (consolidado)
      const any = arr[0];
      const consolidated = {
        // Mostramos datos generales en la fila única
        Project: any.Project,
        Gobernacion: any.CardName,
        FechaEntrega: any.DocDueDate || entDate || "",
        RutaIni: start_route,
        RutaFin: end_route,
        Orders: arr, // <- todas las líneas de TODAS las OV
      };

      setOrders([consolidated]);
      // Limpiar preparaciones previas
      preparedBufferRef.current = {};
      setPreparedByItem({});
      setInputValues({});
    } catch (e) {
      console.error(e);
      setError("Error al obtener órdenes de ventas.");
    } finally {
      setLoading(false);
    }
  };

  // ── Buscar artículos alternativos (consolidado) ───────────
  const fetchAlternativos = async (row) => {
    setAltLoading(true);
    try {
      const sapUser = JSON.parse(localStorage.getItem("sapUser"));
      const Company = sapUser?.CompanyDB || "";
      // Buscar el primer WhsCode de las líneas subyacentes para este ítem
      const firstLine = orders[0]?.Orders?.find(
        (ln) => ln.ItemCode === row.ItemCode
      );
      const whsCode = firstLine?.WhsCode || "";
      const { data } = await apiClient.get("/inventario/alternativos", {
        params: { itemCode: row.ItemCode, whsCode, company: Company },
      });
      const arr = Array.isArray(data) ? data : [];
      setAltModal({ row, alternatives: arr });
    } catch (e) {
      console.error(e);
      alert("Error al obtener artículos alternativos.");
    } finally {
      setAltLoading(false);
    }
  };

  // ── Sustituir TODAS las líneas de un ítem por el alternativo ──
  const aplicarAlternativo = async (alt) => {
    if (!altModal || altSubmitting) return;
    const { row } = altModal;
    setAltSubmitting(true);
    try {
      // Encontrar TODAS las líneas subyacentes con este ItemCode
      const lineasAfectadas = (orders[0]?.Orders || []).filter(
        (ln) => ln.ItemCode === row.ItemCode
      );

      // Sustituir cada línea en SAP (son líneas de distintas OV)
      const errores = [];
      for (const ln of lineasAfectadas) {
        try {
          const resp = await apiClient.patch("/inventario/sustituir-item", {
            docEntry: ln.DocEntry,
            lineNum: ln.LineNum,
            newItemCode: alt.ItemCode,
          });
          if (resp.data?.status !== "success") {
            errores.push(`OV ${ln.DocEntry} Línea ${ln.LineNum}: ${resp.data?.message || "Error"}`);
          }
        } catch (err) {
          const msg = err?.response?.data?.message || err?.response?.data?.error || "Error de conexión";
          errores.push(`OV ${ln.DocEntry} Línea ${ln.LineNum}: ${msg}`);
        }
      }

      if (errores.length > 0 && errores.length === lineasAfectadas.length) {
        alert(`Error al sustituir en todas las líneas:\n${errores.join("\n")}`);
        return;
      }

      if (errores.length > 0) {
        alert(`Se sustituyeron ${lineasAfectadas.length - errores.length} de ${lineasAfectadas.length} líneas.\nErrores:\n${errores.join("\n")}`);
      }

      // Actualizar estado local: reemplazar el ItemCode en TODAS las líneas subyacentes
      setOrders((prev) =>
        prev.map((consolidated) => ({
          ...consolidated,
          Orders: consolidated.Orders.map((ln) => {
            if (ln.ItemCode !== row.ItemCode) return ln;
            return {
              ...ln,
              ItemCodeOriginal: ln.ItemCodeOriginal || ln.ItemCode,
              DscriptionOriginal: ln.DscriptionOriginal || ln.Dscription,
              ItemCode: alt.ItemCode,
              Dscription: alt.Dscription || alt.ItemName || alt.ItemCode,
              OnHand: alt.OnHand != null ? alt.OnHand : ln.OnHand,
              SalUnitMsr: alt.SalUnitMsr || alt.InvntryUom || ln.SalUnitMsr,
              sustituido: true,
            };
          }),
        }))
      );

      // Calcular la cantidad ajustada según U_AjusteNeto del artículo sustituto
      // (ej: 100 KG - 16% = 84 KG). Base: cantidad total del ítem consolidado.
      const ajuste = parseFloat(alt.AjusteNeto) || 0;
      const cantOriginal = parseFloat(row.Quantity || 0) || 0;
      const cantAjustada = Number((cantOriginal * (1 - ajuste / 100)).toFixed(6));

      // Limpiar preparaciones del ítem viejo y precargar el nuevo con la cant. ajustada
      const oldKey = `${row.ItemCode}|${row.SalUnitMsr}`;
      const newUM = alt.SalUnitMsr || alt.InvntryUom || row.SalUnitMsr;
      const newKey = `${alt.ItemCode}|${newUM}`;
      setPreparedByItem((prev) => {
        const next = { ...prev };
        delete next[oldKey];
        next[newKey] = cantAjustada;
        return next;
      });
      preparedBufferRef.current = { ...preparedBufferRef.current };
      delete preparedBufferRef.current[oldKey];
      preparedBufferRef.current[newKey] = cantAjustada;
      setInputValues((prev) => {
        const next = { ...prev };
        delete next[oldKey];
        next[newKey] = String(cantAjustada);
        return next;
      });

      setAltModal(null);
    } catch (e) {
      console.error("Error al sustituir artículo:", e);
      alert("Error inesperado al sustituir artículo.");
    } finally {
      setAltSubmitting(false);
    }
  };

  // ── Revertir sustitución de un ítem consolidado ───────────
  const revertirAlternativo = async (row) => {
    if (!row.ItemCodeOriginal) return;
    setAltSubmitting(true);
    try {
      // Encontrar TODAS las líneas subyacentes sustituidas con este ItemCode
      const lineasAfectadas = (orders[0]?.Orders || []).filter(
        (ln) => ln.ItemCode === row.ItemCode && ln.ItemCodeOriginal
      );

      const errores = [];
      for (const ln of lineasAfectadas) {
        try {
          const resp = await apiClient.patch("/inventario/revertir-item", {
            docEntry: ln.DocEntry,
            lineNum: ln.LineNum,
            originalItemCode: ln.ItemCodeOriginal,
          });
          if (resp.data?.status !== "success") {
            errores.push(`OV ${ln.DocEntry} Línea ${ln.LineNum}: ${resp.data?.message || "Error"}`);
          }
        } catch (err) {
          const msg = err?.response?.data?.message || err?.response?.data?.error || "Error de conexión";
          errores.push(`OV ${ln.DocEntry} Línea ${ln.LineNum}: ${msg}`);
        }
      }

      if (errores.length > 0 && errores.length === lineasAfectadas.length) {
        alert(`Error al revertir en todas las líneas:\n${errores.join("\n")}`);
        return;
      }

      if (errores.length > 0) {
        alert(`Se revirtieron ${lineasAfectadas.length - errores.length} de ${lineasAfectadas.length} líneas.\nErrores:\n${errores.join("\n")}`);
      }

      // Actualizar estado local
      setOrders((prev) =>
        prev.map((consolidated) => ({
          ...consolidated,
          Orders: consolidated.Orders.map((ln) => {
            if (ln.ItemCode !== row.ItemCode || !ln.ItemCodeOriginal) return ln;
            return {
              ...ln,
              ItemCode: ln.ItemCodeOriginal,
              Dscription: ln.DscriptionOriginal,
              ItemCodeOriginal: undefined,
              DscriptionOriginal: undefined,
              sustituido: false,
            };
          }),
        }))
      );

      // Ajustar preparaciones: al revertir se restaura la cantidad original
      // (se descarta el ajuste que se había aplicado al sustituir).
      const oldKey = `${row.ItemCode}|${row.SalUnitMsr}`;
      const firstLn = lineasAfectadas[0];
      if (firstLn) {
        const origUM = firstLn.SalUnitMsr || row.SalUnitMsr;
        const newKey = `${firstLn.ItemCodeOriginal}|${origUM}`;
        const cantOriginal = Number(row.Quantity) || 0;
        setPreparedByItem((prev) => {
          const next = { ...prev };
          delete next[oldKey];
          next[newKey] = cantOriginal;
          return next;
        });
        delete preparedBufferRef.current[oldKey];
        preparedBufferRef.current[newKey] = cantOriginal;
        setInputValues((prev) => {
          const next = { ...prev };
          delete next[oldKey];
          next[newKey] = String(cantOriginal);
          return next;
        });
      }
    } catch (e) {
      console.error("Error al revertir artículo:", e);
      alert("Error inesperado al revertir artículo.");
    } finally {
      setAltSubmitting(false);
    }
  };

  const filteredOrders = useMemo(() => {
    if (!searchTerm) return orders;
    const q = searchTerm.toLowerCase();
    return orders.filter(
      (ov) =>
        (ov.Gobernacion || "").toLowerCase().includes(q) ||
        (ov.Project || "").toLowerCase().includes(q)
    );
  }, [orders, searchTerm]);

  // ---- Expandible con columna "Cantidad a preparar"
  const ExpandedComponent = ({ data }) => {
    const groupedItems = useMemo(() => {
      const acc = {};
      data.Orders.forEach((it) => {
        const key = `${it.ItemCode}|${it.Dscription}|${it.SalUnitMsr}`;
        if (!acc[key]) {
          acc[key] = {
            ItemCode: it.ItemCode,
            Dscription: it.Dscription,
            SalUnitMsr: it.SalUnitMsr,
            Quantity: 0,
            OnHand: Number(it.OnHand),
            sustituido: it.sustituido || false,
            ItemCodeOriginal: it.ItemCodeOriginal || null,
            WhsCode: it.WhsCode || "",
          };
        }
        acc[key].Quantity += Number(it.Quantity) || 0;
        // Conservar el primer OnHand válido si existe; no sumar
        if ((acc[key].OnHand == null || isNaN(acc[key].OnHand)) && !isNaN(Number(it.OnHand))) {
          acc[key].OnHand = Number(it.OnHand);
        }
        // Si al menos una línea fue sustituida, marcar el grupo
        if (it.sustituido) {
          acc[key].sustituido = true;
          acc[key].ItemCodeOriginal = it.ItemCodeOriginal;
        }
      });
      return Object.values(acc);
    }, [data.Orders]);

    // clave SOLO para estado (global por ítem)
    const makeDataKey = (row) => `${row.ItemCode}|${row.SalUnitMsr}`;

    // Helpers de decimales
    const round6 = (n) => {
      const x = Number(n);
      if (!isFinite(x)) return 0;
      return Number(x.toFixed(6));
    };
    const toInputString = (n) => {
      if (n === null || n === undefined || isNaN(Number(n))) return "";
      const s = Number(n).toFixed(6);
      // quitar ceros a la derecha y un punto final innecesario
      return s.replace(/\.0+$/, "").replace(/(\.\d*?)0+$/, "$1");
    };
    const normalizeToNumber = (raw) => {
      if (raw === null || raw === undefined) return NaN;
      const s = String(raw).trim();
      if (!s) return NaN;
      // Si hay coma decimal, quitamos puntos de miles y convertimos coma a punto.
      if (s.includes(",")) {
        return parseFloat(s.replace(/\./g, "").replace(",", "."));
      }
      // Si no hay coma, respetamos el punto como decimal (no lo quitamos).
      return parseFloat(s);
    };

    // Preservar scroll solo del contenedor de la tabla expandida
    const scrollRef = useRef(null);
    useEffect(() => {
      const el = scrollRef.current;
      if (!el) return;
      const onScroll = () => {
        expandedScrollTopRef.current = el.scrollTop;
      };
      el.addEventListener("scroll", onScroll, { passive: true });
      return () => el.removeEventListener("scroll", onScroll);
    }, []);
    useLayoutEffect(() => {
      const el = scrollRef.current;
      if (el) el.scrollTop = expandedScrollTopRef.current;
    }, [inputValues, preparedByItem]);

    // Prefill defaults: for non-AE items, set prepared = total quantity if not set yet
    useEffect(() => {
      const toAdd = {};
      groupedItems.forEach((row) => {
        const k = makeDataKey(row);
        const isBlocked = String(row.ItemCode || "").toUpperCase().startsWith("AE");
        if (!isBlocked && preparedByItem[k] == null) {
          toAdd[k] = Number(row.Quantity) || 0;
        }
      });
      if (Object.keys(toAdd).length) {
        preparedBufferRef.current = { ...preparedBufferRef.current, ...toAdd };
        setPreparedByItem((prev) => ({ ...prev, ...toAdd }));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupedItems]);

    // confirmar sin perder foco
    const commitPrepared = (row, raw) => {
      const max = Number(row.Quantity) || 0;
      let v = normalizeToNumber(raw);
      if (isNaN(v) || v < 0) v = 0;
      v = round6(v);
      if (v > max) v = max;

      const k = makeDataKey(row);
      preparedBufferRef.current[k] = v;
      setPreparedByItem((prev) => ({ ...prev, [k]: v }));
      // Mantener el valor visible formateado (incluido 0)
      setInputValues((prev) => ({ ...prev, [k]: toInputString(v) }));
    };

    // Calcular total desde el valor que se ve en pantalla (inputValues) para que sea en vivo
    const totalPreparado = groupedItems.reduce((sum, r) => {
      const k = makeDataKey(r);
      const raw = inputValues[k] ?? preparedByItem[k];
      const n = normalizeToNumber(raw);
      return sum + (isNaN(n) ? 0 : n);
    }, 0);

    return (
      <div className="p-2" style={{ width: "100%" }}>
        <h6 style={{ fontSize: 14 }}>Resumen de Insumos</h6>
        <small className="text-muted d-block mb-2">
          * Los códigos que comienzan con <strong>AE</strong> no permiten cargar
          cantidades.
        </small>

        <div
          ref={scrollRef}
          style={{
            overflowX: "auto",
            overflowY: "auto",
            width: "100%",
            maxHeight: "70vh", // scroll interno de la tabla
          }}
        >
          <div style={{ minWidth: "1100px" }}>
            <table
              className="table table-sm table-bordered"
              style={{ fontSize: 14, width: "100%", whiteSpace: "nowrap" }}
            >
              <thead>
                <tr>
                  <th style={{ minWidth: 120 }}>Insumo</th>
                  <th style={{ minWidth: 300 }}>Descripción</th>
                  <th style={{ minWidth: 140, textAlign: "right" }}>
                    Total Cantidad
                  </th>
                  <th style={{ minWidth: 140 }}>Unidad de Medida</th>
                  <th style={{ minWidth: 140, textAlign: "right" }}>Stock (OnHand)</th>
                  <th style={{ minWidth: 180, textAlign: "right" }}>
                    Cantidad a preparar
                  </th>
                  <th style={{ minWidth: 50, textAlign: "center" }}>Alt</th>
                </tr>
              </thead>
              <tbody>
                {groupedItems.map((row, idx) => {
                  const dataKey = makeDataKey(row); // para estado
                  const domKey = `${row.ItemCode}|${row.Dscription}|${row.SalUnitMsr}|${idx}`; // key única para React
                  const isBlocked = String(row.ItemCode || "")
                    .toUpperCase()
                    .startsWith("AE");
                  const valueForInput = inputValues[dataKey] ??
                    (preparedByItem[dataKey] !== undefined
                      ? toInputString(preparedByItem[dataKey])
                      : toInputString(Number(row.Quantity)));

                  const stockItem = isNaN(Number(row.OnHand)) ? null : Number(row.OnHand);
                  const stockInsuficiente = stockItem !== null && stockItem < Number(row.Quantity);

                  return (
                    <tr key={domKey} style={row.sustituido ? { background: "#fff9e6" } : {}}>
                      <td>
                        {row.ItemCode}
                        {row.sustituido && (
                          <span className="badge bg-warning text-dark ms-1" style={{ fontSize: 9 }}>
                            Alt
                          </span>
                        )}
                      </td>
                      <td style={{ whiteSpace: "normal" }}>{row.Dscription}</td>
                      <td style={{ textAlign: "right" }}>
                        {Number(row.Quantity).toLocaleString("es-ES")}
                      </td>
                      <td>{row.SalUnitMsr}</td>
                      <td style={{ textAlign: "right" }}>
                        {isNaN(Number(row.OnHand)) ? "" : Number(row.OnHand).toLocaleString("es-ES")}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <input
                          type="number"
                          className="form-control form-control-sm text-end"
                          min={0}
                          step="any"
                          max={row.Quantity || 0}
                          value={isBlocked ? "" : valueForInput}
                          onChange={(e) => {
                            const val = e.target.value;
                            preparedBufferRef.current[dataKey] = val;
                            setInputValues((prev) => ({ ...prev, [dataKey]: val }));
                          }}
                          onBlur={(e) => commitPrepared(row, e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") e.currentTarget.blur();
                          }}
                          disabled={isBlocked}
                          title={
                            isBlocked
                              ? "No se puede cargar cantidad para ítems AE"
                              : "Ingrese la cantidad a preparar (no puede superar el total)"
                          }
                          style={
                            isBlocked
                              ? { background: "#f1f1f1", cursor: "not-allowed" }
                              : {}
                          }
                        />
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {isBlocked ? null : row.sustituido ? (
                          <button
                            className="btn btn-outline-secondary btn-sm p-0"
                            style={{ fontSize: 11, lineHeight: 1, width: 28, height: 24 }}
                            title="Revertir al artículo original"
                            onClick={() => revertirAlternativo(row)}
                            disabled={altSubmitting}
                          >
                            ↩
                          </button>
                        ) : stockInsuficiente ? (
                          <button
                            className="btn btn-outline-warning btn-sm p-0"
                            style={{ fontSize: 11, lineHeight: 1, width: 28, height: 24 }}
                            title="Buscar artículo alternativo"
                            onClick={() => fetchAlternativos(row)}
                            disabled={altLoading}
                          >
                            🔄
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={6} className="text-end">
                    <strong>Total preparado</strong>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <strong>{totalPreparado.toLocaleString("es-ES")}</strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // ---- Export PDF (sin cambios funcionales)
  const exportarPDF = () => {
    const datos = orders.flatMap((o) => o.Orders);
    const grouped = datos.reduce((acc, item) => {
      const key = `${item.ItemCode}|${item.Dscription}|${item.SalUnitMsr}`;
      if (!acc[key]) {
        acc[key] = {
          ItemCode: item.ItemCode,
          Dscription: item.Dscription,
          SalUnitMsr: item.SalUnitMsr,
          Quantity: 0,
        };
      }
      acc[key].Quantity += parseFloat(item.Quantity) || 0;
      return acc;
    }, {});
    const rows = Object.values(grouped);

    let checklistItems = [];
    orders.forEach((o) => {
      o.Orders.forEach((line) => {
        const key = `${line.DocNum}|${line.Name}|${line.U_Serv}`;
        if (!checklistItems.some((i) => `${i.DocNum}|${i.Name}|${i.U_Serv}` === key)) {
          checklistItems.push({
            DocNum: line.DocNum,
            Name: line.Name,
            U_Serv: line.U_Serv,
          });
        }
      });
    });
    checklistItems.sort((a, b) =>
      a.U_Serv === b.U_Serv
        ? a.DocNum.toString().localeCompare(b.DocNum.toString())
        : a.U_Serv.localeCompare(b.U_Serv)
    );

    const content = [
      { text: "PRODUCTOS CONSOLIDADOS", bold: true, fontSize: 16, margin: [0, 0, 0, 10] },
      { text: `Gobernación: ${orders[0]?.Gobernacion || ""}`, fontSize: 12 },
      { text: `Ruta: ${start_route}`, fontSize: 12 },
      { text: `Fecha de Entrega: ${entDate}`, fontSize: 12 },
      { text: "\n" },
      {
        table: {
          headerRows: 1,
          widths: ["auto", "*", "auto", "auto", "auto", "auto"],
          body: [
            [
              { text: "Producto", bold: true },
              { text: "Descripción", bold: true },
              { text: "UM", bold: true },
              { text: "Cantidad Total", bold: true },
              { text: "Nro. de Lote", bold: true },
              { text: "Peso Total", bold: true },
            ],
            ...rows.map((row) => [
              row.ItemCode,
              row.Dscription,
              row.SalUnitMsr,
              row.Quantity.toLocaleString("es-ES"),
              entDate,
              "",
            ]),
          ],
        },
        fontSize: 11,
      },
      {
        text: "Checklist de Entrega por Escuela",
        bold: true,
        fontSize: 11,
        margin: [0, 10, 0, 4],
        pageBreak: "before",
      },
      {
        table: {
          headerRows: 1,
          widths: ["auto", "*", "auto", "auto"],
          body: [
            [
              { text: "DocNum", bold: true },
              { text: "Escuela", bold: true },
              { text: "Servicio", bold: true },
              { text: "Check", bold: true },
            ],
            ...checklistItems.map((item) => [
              item.DocNum,
              item.Name,
              item.U_Serv,
              "",
            ]),
          ],
        },
        fontSize: 12,
      },
    ];

    pdfMake.createPdf({ content, pageOrientation: "portrait", defaultStyle: { fontSize: 8 } }).open();
  };

  // ---- Construir entregas desde preparedByItem (distribuye por líneas y OpenQty)
  const handleConfirmAndDeliver = async () => {
    try {
      setSubmitting(true);
      setError("");

      // Normalizador seguro: si hay coma decimal, quita puntos de miles y cambia coma por punto;
      // si no hay coma, respeta el punto como decimal.
      const normalizePreparedValue = (raw) => {
        if (raw === null || raw === undefined) return NaN;
        const s = String(raw).trim();
        if (!s) return NaN;
        if (s.includes(",")) {
          return parseFloat(s.replace(/\./g, "").replace(",", "."));
        }
        return parseFloat(s);
      };
      const round6 = (n) => {
        const x = Number(n);
        if (!isFinite(x)) return 0;
        return Number(x.toFixed(6));
      };

      // helpers (déjalos donde los tengas definidos)
      const toISODate = (d) => {
        if (!d) return "";
        const dt = new Date(d);
        return isNaN(dt) ? String(d).slice(0, 10) : dt.toISOString().slice(0, 10);
      };
      const todayISO = new Date().toISOString().slice(0, 10);

      // Consolidado único (ya armado en fetchInsumos)
      const cons = orders[0];
      if (!cons) { alert("No hay datos para generar entregas."); return; }

      // 1) Mapa global de preparación por ItemCode|UM (sin distribuir)
      // Sync buffer -> state to ensure latest typed numbers are considered
      const merged = { ...preparedByItem, ...preparedBufferRef.current };
      const preparedMap = Object.keys(merged).reduce((acc, k) => {
        const num = normalizePreparedValue(merged[k]);
        if (!isNaN(num)) acc[k] = round6(num);
        return acc;
      }, {});
      const hasAnyPrepared = Object.values(preparedMap).some(v => Number(v) > 0);
      if (!hasAnyPrepared) {
        alert("No hay cantidades preparadas. Si no modificas nada, ahora se precargan con el total para ítems no AE. Verifica que el consolidado tenga líneas y que no todas sean AE.");
        return;
      }

      // 2) Iteramos TODAS las líneas de TODAS las OV y
      //    SI el ítem tiene preparación (>0), agregamos la línea
      const deliveriesByOV = {};
      const allLines = [...cons.Orders].sort(
        (a, b) => (a.DocEntry ?? 0) - (b.DocEntry ?? 0) || (a.LineNum ?? 0) - (b.LineNum ?? 0)
      );

      allLines.forEach((ln) => {
        const prepKey = `${ln.ItemCode}|${ln.SalUnitMsr}`;
        const prepared = Number(preparedMap[prepKey]) || 0;
        if (prepared <= 0) return; // solo generamos para ítems con preparación

        // Cantidad ORIGINAL de la línea (mejor si tu backend te envía OrderedQty)
        const orderedQty = Number(
          ln.OrderedQty ?? ln.QuantityOrdered ?? ln.OrigQuantity ?? ln.Quantity ?? 0
        );

        // Crear encabezado por OV si no existe
        if (!deliveriesByOV[ln.DocEntry]) {
          deliveriesByOV[ln.DocEntry] = {
            DocDate: todayISO,                           // hoy
            DocDueDate: toISODate(cons.FechaEntrega),    // fecha de entrega elegida
            CardCode: ln.CardCode || cons.Orders?.[0]?.CardCode || "",
            ContactPersonCode: ln.Institucion ?? ln.CntctCode ?? null,
            U_Enviar: "NO",
            DocumentLines: [],
          };
        }

        // Agregar línea basada en la OV (SIN repartir 'prepared'; se repite por OV)
        deliveriesByOV[ln.DocEntry].DocumentLines.push({
          BaseType: 17,
          BaseEntry: ln.DocEntry,
          BaseLine: ln.LineNum,
          ItemCode: ln.ItemCode,
          UnitPrice: ln.AvgPrice,
          Quantity: orderedQty,        // cantidad ORIGINAL de la línea de la OV
          U_Preparacion: prepared,     // MISMO valor preparado global por ítem
        });
      });

      // 3) Array final (una entrega por OV)
      const deliveries = Object.values(deliveriesByOV).filter(d => d.DocumentLines.length);
      if (!deliveries.length) { alert("No se pudo generar ninguna entrega."); return; }

      console.log(deliveries);
      const resp = await apiClient.post("/inventario/entregas", { orders: deliveries }, { timeout: 600000 });
      const results = resp?.data || [];
      const ok = results.filter((r) => r.status === "success").length;
      const bad = results.filter((r) => r.status === "error").length;

      alert(
        `${ok ? `${ok} entregas creadas con éxito.\n` : ""}${
          bad ? `${bad} entregas con error.\n` : ""
        }${!ok && !bad ? "Proceso finalizado." : ""}`
      );

      await fetchInsumos();
    } catch (err) {
      console.error("Error generando entregas", err);
      setError("Ocurrió un error al generar las entregas.");
      alert("Ocurrió un error al generar las entregas.");
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    { name: "Proyecto", selector: (row) => row.Project, sortable: true, width: "240px" },
    { name: "Gobernación", selector: (row) => row.Gobernacion, sortable: true, width: "360px" },
    { name: "Ruta Inicial", selector: (row) => row.RutaIni, sortable: true, width: "120px" },
    { name: "Ruta Final", selector: (row) => row.RutaFin, sortable: true, width: "120px" },
    { name: "Fecha Entrega", selector: (row) => row.FechaEntrega, sortable: true, width: "160px" },
  ];

  return (
    <div className="container mt-4">
      <h2>Preparación de Insumos</h2>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="row">
        <div className="col-md-3">
          <label>Tipo de Servicio:</label>
          <Select options={SERVICE_TYPES} value={serviceType} onChange={setserviceType} placeholder="Seleccione un Servicio..." isSearchable />
        </div>

        <div className="col-md-5">
          <label>Código de Proyecto:</label>
          <Select options={projects} value={project} onChange={setproject} placeholder="Seleccione un proyecto..." isSearchable />
        </div>

        <div className="col-md-2">
          <label>Fecha de Entrega:</label>
          <input type="date" className="form-control" value={entDate} onChange={(e) => setEntDate(e.target.value)} />
        </div>

        <div className="col-md-1">
          <label>Ruta:</label>
          <input type="number" className="form-control" value={start_route} onChange={(e) => setStartRoute(e.target.value)} />
        </div>
      </div>

      <div className="d-flex gap-2 mt-3 flex-wrap">
        <button className="btn btn-success" onClick={fetchInsumos} disabled={loading}>
          {loading ? "Cargando..." : "Obtener Consolidado"}
        </button>

        <button className="btn btn-primary" onClick={exportarPDF} disabled={loading || !orders.length}>
          Imprimir Consolidado
        </button>

        <button
          className="btn btn-warning"
          onClick={() => {
            preparedBufferRef.current = {};
            setPreparedByItem({});
            setInputValues({});
          }}
          disabled={!orders.length || submitting}
        >
          Limpiar Preparaciones
        </button>

        <button className="btn btn-dark" onClick={handleConfirmAndDeliver} disabled={!orders.length || submitting}>
          {submitting ? "Generando Entregas..." : "Confirmar y Generar Entregas"}
        </button>
      </div>

      <DataTable
        className="mt-3"
        columns={columns}
        data={filteredOrders}
        expandableRows
        expandableRowsComponent={ExpandedComponent}
        expandableRowExpanded={() => true}
        pagination
        highlightOnHover
        dense
      />

      {/* ── Modal de artículos alternativos ── */}
      {altModal && (
        <>
          <div className="modal-backdrop show" style={{ zIndex: 1060 }}></div>
          <div className="modal show d-block" tabIndex="-1" role="dialog" style={{ zIndex: 1070 }}>
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    Artículos Alternativos para <strong>{altModal.row.ItemCode}</strong>
                  </h5>
                  <button type="button" className="btn-close" onClick={() => setAltModal(null)}></button>
                </div>
                <div className="modal-body">
                  <p className="mb-2">
                    <strong>Artículo original:</strong> {altModal.row.ItemCode} — {altModal.row.Dscription}
                    <br />
                    <strong>Stock actual:</strong>{" "}
                    <span className="text-danger fw-bold">
                      {altModal.row.OnHand != null ? parseFloat(altModal.row.OnHand).toLocaleString("es-ES") : "—"}
                    </span>
                    {" | "}
                    <strong>Cant. total:</strong> {parseFloat(altModal.row.Quantity || 0).toLocaleString("es-ES")}
                  </p>
                  {altModal.alternatives.length === 0 ? (
                    <div className="alert alert-warning mb-0">
                      No se encontraron artículos alternativos para este producto.
                    </div>
                  ) : (
                    <table className="table table-sm table-hover table-bordered" style={{ fontSize: 13 }}>
                      <thead>
                        <tr>
                          <th>ItemCode</th>
                          <th>Descripción</th>
                          <th>UM</th>
                          <th style={{ textAlign: "right" }}>Stock</th>
                          <th style={{ textAlign: "right" }}>Ajuste %</th>
                          <th style={{ textAlign: "right" }}>Cant. ajustada</th>
                          <th style={{ textAlign: "center" }}>Acción</th>
                        </tr>
                      </thead>
                      <tbody>
                        {altModal.alternatives.map((alt, idx) => {
                          const altStock = alt.OnHand != null ? parseFloat(alt.OnHand) || 0 : 0;
                          const ajuste = parseFloat(alt.AjusteNeto) || 0;
                          const cantOriginal = parseFloat(altModal.row.Quantity || 0) || 0;
                          const cantAjustada = Number((cantOriginal * (1 - ajuste / 100)).toFixed(6));
                          return (
                            <tr key={idx}>
                              <td>{alt.ItemCode}</td>
                              <td>{alt.Dscription || alt.ItemName || ""}</td>
                              <td>{alt.SalUnitMsr || alt.InvntryUom || ""}</td>
                              <td style={{ textAlign: "right" }}>
                                <span className={altStock <= 0 ? "text-danger fw-bold" : "text-success fw-bold"}>
                                  {altStock.toLocaleString("es-ES")}
                                </span>
                              </td>
                              <td style={{ textAlign: "right" }}>
                                {ajuste.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %
                              </td>
                              <td style={{ textAlign: "right", fontWeight: "bold" }}>
                                {cantAjustada.toLocaleString("es-ES")}
                              </td>
                              <td style={{ textAlign: "center" }}>
                                <button
                                  className="btn btn-sm btn-success"
                                  onClick={() => aplicarAlternativo(alt)}
                                  disabled={altStock <= 0 || altSubmitting}
                                  title={altStock <= 0 ? "Sin stock disponible" : "Sustituir por este artículo"}
                                >
                                  {altSubmitting ? "Guardando..." : "Sustituir"}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setAltModal(null)}>
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PlanifLines;
